/**
 * AI Command Center — Main page with tabbed navigation
 *
 * 4 core tabs (Agents, Models, Skills, SQA) + slot comments for WQ-165/166/167.
 * Pattern: CloudOpsPage section-based loading with dedicated service files.
 *
 * Phase 4.7 — WQ-164
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { CpuIcon, RefreshIcon, SearchIcon } from '../components/common/Icons';
import AgentDashboard from '../components/ai/AgentDashboard';
import SkillLibrary from '../components/ai/SkillLibrary';
import SQADashboard from '../components/ai/SQADashboard';
import AnalyticsHub from '../components/ai/AnalyticsHub';
import ConnectorConsole from '../components/ai/ConnectorConsole';
import CommissioningPanel from '../components/ai/CommissioningPanel';
import SLMDeploymentConsole from '../components/ai/SLMDeploymentConsole';
import PromptManagement from '../components/ai/PromptManagement';

interface AICommandCenterProps {
  defaultTab?: string;
}

// === Models Tab (inline — extracted from LLMPage.tsx) ===

interface LLMModel {
  id: string;
  name: string;
  provider: string;
  model_id?: string;
  type?: string;
  status: string;
  context_length?: number;
  context_window?: number;
  cost_per_1k_tokens?: number;
  cost_input_per_1m?: number;
  is_default?: boolean;
  capabilities?: string[];
  origin_country?: string;
}

const STATUS_COLORS: Record<string, string> = {
  active: '#22c55e', production: '#22c55e', approved: '#3b82f6',
  candidate: '#eab308', testing: '#a855f7',
  inactive: '#6b7280', deprecated: '#f97316', retired: '#dc2626',
  error: '#dc2626',
};

const ModelsTab: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [models, setModels] = useState<LLMModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchModels = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch('/api/admin/ai/models');
      if (response.ok) {
        const data = await response.json();
        setModels(Array.isArray(data) ? data : data.models || []);
      } else {
        // Fallback to legacy endpoint
        const legacyResponse = await authFetch('/api/admin/llm/models');
        if (legacyResponse.ok) {
          const data = await legacyResponse.json();
          setModels(Array.isArray(data) ? data : data.models || []);
        } else {
          setError('Failed to fetch models');
        }
      }
    } catch (err) {
      console.error('Failed to fetch models:', err);
      setError('Failed to connect to API');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchModels();
  }, [authLoading]);

  const filtered = models.filter(m =>
    !searchQuery ||
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.provider?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.model_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading && models.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading models...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <SearchIcon size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input
            type="text" placeholder="Search models..." value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '8px 8px 8px 32px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
          />
        </div>
        <button onClick={fetchModels} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6,
          border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
          background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <RefreshIcon size={14} /> Refresh
        </button>
      </div>

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          {searchQuery ? 'No models match your search.' : 'No models registered in the platform registry.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Provider</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Model ID</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Status</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Origin</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Context</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(model => (
                <tr key={model.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 600 }}>{model.name}</td>
                  <td style={{ padding: '8px 12px' }}>{model.provider}</td>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                    {model.model_id || model.type || '—'}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 10,
                      background: `${STATUS_COLORS[model.status] || '#888'}20`,
                      color: STATUS_COLORS[model.status] || '#888',
                    }}>{model.status}</span>
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 12px', fontSize: 12 }}>
                    {model.origin_country || '—'}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px 12px', fontSize: 12 }}>
                    {(model.context_window || model.context_length)
                      ? `${((model.context_window || model.context_length || 0) / 1000).toFixed(0)}K`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// === Main Page ===

interface KillSwitchStatus {
  active: boolean;
  reason?: string;
  activated_by?: string;
  activated_at?: string;
  latency_ms?: number;
}

const AICommandCenterPage: React.FC<AICommandCenterProps> = ({ defaultTab = 'agents' }) => {
  const { authFetch } = useAuth();
  const [killSwitch, setKillSwitch] = useState<KillSwitchStatus>({ active: false });
  const [showKillPanel, setShowKillPanel] = useState(false);
  const [killReason, setKillReason] = useState('');
  const [killActionLoading, setKillActionLoading] = useState(false);
  const [killTestResult, setKillTestResult] = useState<string | null>(null);

  const checkKillSwitch = async () => {
    try {
      const res = await authFetch('/api/admin/ai/emergency/kill-switch');
      if (res.ok) {
        const data = await res.json();
        setKillSwitch({
          active: data.active === true,
          reason: data.reason,
          activated_by: data.activated_by,
          activated_at: data.activated_at,
        });
      }
    } catch {
      // Non-critical — don't block on kill switch status
    }
  };

  useEffect(() => {
    checkKillSwitch();
    const interval = setInterval(checkKillSwitch, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleActivate = async () => {
    if (!killReason.trim()) return;
    setKillActionLoading(true);
    try {
      const res = await authFetch('/api/admin/ai/emergency/kill-switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: killReason.trim() }),
      });
      if (res.ok) {
        setKillReason('');
        await checkKillSwitch();
      }
    } catch { /* handled by polling */ }
    setKillActionLoading(false);
  };

  const handleDeactivate = async () => {
    setKillActionLoading(true);
    try {
      await authFetch('/api/admin/ai/emergency/kill-switch', { method: 'DELETE' });
      await checkKillSwitch();
    } catch { /* handled by polling */ }
    setKillActionLoading(false);
  };

  const handleTest = async () => {
    setKillActionLoading(true);
    setKillTestResult(null);
    try {
      const start = Date.now();
      const res = await authFetch('/api/admin/ai/emergency/kill-switch/test', { method: 'POST' });
      const latency = Date.now() - start;
      if (res.ok) {
        const data = await res.json();
        setKillTestResult(`Test passed in ${data.latency_ms ?? latency}ms`);
      } else {
        setKillTestResult(`Test failed: ${res.status}`);
      }
    } catch (err) {
      setKillTestResult(`Test error: ${err instanceof Error ? err.message : 'unknown'}`);
    }
    setKillActionLoading(false);
  };

  return (
    <div style={{ padding: '0 16px' }}>
      {/* Header */}
      <div className="page-header">
        <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CpuIcon size={24} />
          AI Command Center
        </h2>
        <span style={{
          fontSize: 12, padding: '4px 12px', borderRadius: 12, fontWeight: 600,
          background: killSwitch.active ? '#fef2f2' : '#dcfce7',
          color: killSwitch.active ? '#dc2626' : '#166534',
        }}>
          {killSwitch.active ? 'AI HALTED' : 'AI Active'}
        </span>
      </div>

      {/* Kill Switch Card — always visible above tabs */}
      {!killSwitch.active ? (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 16px', borderRadius: 8, marginBottom: 16,
          border: '1px solid rgba(34, 197, 94, 0.2)',
          background: 'rgba(34, 197, 94, 0.05)',
          flexWrap: 'wrap', gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#22c55e' }}>AI Systems Active</span>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>All operations normal</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {killTestResult && (
              <span style={{ fontSize: 11, color: killTestResult.includes('passed') ? '#22c55e' : '#dc2626' }}>
                {killTestResult}
              </span>
            )}
            <button onClick={handleTest} disabled={killActionLoading} style={{
              fontSize: 12, padding: '4px 12px', borderRadius: 6,
              border: '1px solid var(--border)', background: 'var(--bg-secondary)',
              color: 'var(--text-primary)', cursor: 'pointer',
            }}>Test</button>
            <button onClick={() => setShowKillPanel(!showKillPanel)} style={{
              fontSize: 12, padding: '4px 12px', borderRadius: 6,
              border: '1px solid rgba(220, 38, 38, 0.3)', background: 'rgba(220, 38, 38, 0.08)',
              color: '#dc2626', cursor: 'pointer', fontWeight: 600,
            }}>Emergency Halt</button>
          </div>
          {/* Inline expand for activation */}
          {showKillPanel && (
            <div style={{ width: '100%', paddingTop: 8, borderTop: '1px solid rgba(220, 38, 38, 0.15)', marginTop: 4 }}>
              <div style={{ fontSize: 11, color: '#dc2626', marginBottom: 6 }}>
                This will halt ALL AI operations including essential agents. Use only in emergencies.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={killReason} onChange={e => setKillReason(e.target.value)}
                  placeholder="Reason for activation (required)"
                  style={{
                    flex: 1, padding: '6px 10px', borderRadius: 6, fontSize: 12,
                    border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                  }}
                />
                <button onClick={handleActivate} disabled={killActionLoading || !killReason.trim()} style={{
                  fontSize: 12, padding: '6px 16px', borderRadius: 6,
                  border: 'none', fontWeight: 600, cursor: 'pointer',
                  background: '#dc2626', color: '#fff',
                  opacity: (!killReason.trim() || killActionLoading) ? 0.5 : 1,
                }}>
                  {killActionLoading ? 'Activating...' : 'Confirm Halt'}
                </button>
                <button onClick={() => setShowKillPanel(false)} style={{
                  fontSize: 12, padding: '6px 12px', borderRadius: 6,
                  border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)', cursor: 'pointer',
                }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 16,
          border: '1px solid rgba(220, 38, 38, 0.4)',
          background: 'rgba(220, 38, 38, 0.08)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#dc2626', flexShrink: 0 }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: '#dc2626' }}>AI OPERATIONS HALTED</span>
            </div>
            <button onClick={handleDeactivate} disabled={killActionLoading} style={{
              fontSize: 12, padding: '4px 16px', borderRadius: 6,
              border: 'none', background: '#22c55e', color: '#fff',
              cursor: 'pointer', fontWeight: 600,
            }}>
              {killActionLoading ? 'Resuming...' : 'Resume AI Operations'}
            </button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {killSwitch.reason && <>Reason: <strong style={{ color: 'var(--text-primary)' }}>{killSwitch.reason}</strong> · </>}
            {killSwitch.activated_by && <>Activated by: {killSwitch.activated_by} · </>}
            {killSwitch.activated_at && new Date(killSwitch.activated_at).toLocaleString()}
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue={defaultTab}>
        <TabsList style={{ marginBottom: 20 }}>
          <TabsTrigger value="agents">Agents</TabsTrigger>
          <TabsTrigger value="models">Models</TabsTrigger>
          <TabsTrigger value="skills">Skills</TabsTrigger>
          <TabsTrigger value="sqa">Quality (SQA)</TabsTrigger>
          <TabsTrigger value="connectors">Connectors</TabsTrigger>
          <TabsTrigger value="commissioning">Commissioning</TabsTrigger>
          <TabsTrigger value="slm">SLM Serving</TabsTrigger>
          <TabsTrigger value="prompts">Prompts</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          {/* === WQ-167 SLOT: Analytics Tab === */}
        </TabsList>

        <TabsContent value="agents">
          <AgentDashboard />
        </TabsContent>

        <TabsContent value="models">
          <ModelsTab />
        </TabsContent>

        <TabsContent value="skills">
          <SkillLibrary />
        </TabsContent>

        <TabsContent value="sqa">
          <SQADashboard />
        </TabsContent>

        <TabsContent value="connectors">
          <ConnectorConsole />
        </TabsContent>
        <TabsContent value="commissioning">
          <CommissioningPanel />
        </TabsContent>
        <TabsContent value="slm">
          <SLMDeploymentConsole />
        </TabsContent>
        <TabsContent value="prompts">
          <PromptManagement />
        </TabsContent>
        <TabsContent value="analytics">
          <AnalyticsHub />
        </TabsContent>
        {/* === WQ-167 SLOT: Analytics TabsContent === */}
      </Tabs>
    </div>
  );
};

export default AICommandCenterPage;
